import React, { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PagePanel } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { BenefitGroupForm } from '@epi-forms/BenefitGroup/BenefitGroupForm';
import {
  IBenefitDefaultSettings,
  ICreateBenefitGroupForm
} from '@epi-models/pages/BenefitGroups';
import { BenefitType } from '@epi-constants/benefitTypes';
import { IBenefits } from '@epi-models/containers/ManageBenefits';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';
import { useDispatch } from 'react-redux';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { useCreateBenefitGroupMutation } from '@epi-api/benefitGroups';
import { showNotification } from '@epi-actions/overlays';
import { CREATE_BENEFIT_GROUPS_CACHE_KEY } from '@epi-constants/benefitGroups';
import { toDot } from '@epi-helpers/commaHelper';

interface CreateBenefitGroupFormProps {
  defaultSettings: IBenefitDefaultSettings;
}

const createDefaultValuesFromSettings = (
  defaultSettings: IBenefitDefaultSettings
) => {
  const settings = {};

  for (const { maxAmount, benefitType } of defaultSettings.contractedBenefits) {
    if (benefitType === BenefitType.Lunch) {
      settings[benefitType] = {
        maxAmountPerPeriod: maxAmount,
        isMaximumSpendingLimit:
          defaultSettings.lunchGlobalSettings?.isMaximumSpendingLimit,
        fundsLoadingType: defaultSettings.lunchGlobalSettings?.fundsLoadingType,
        recurringTopupRequestEnabled:
          defaultSettings.lunchGlobalSettings?.recurringTopupRequestEnabled ??
          false
      };
    } else {
      settings[benefitType] = { maxAmountPerPeriod: maxAmount };
    }
  }

  return settings as Record<
    IBenefits,
    {
      maxAmountPerPeriod: number;
      isMaximumSpendingLimit: boolean;
      fundsLoadingType: LunchFundsLoadingValues;
      recurringTopupRequestEnabled: boolean;
    }
  >;
};

const formatValuesToDTO = (values: ICreateBenefitGroupForm) => {
  const formattedSettings = {};

  for (const [benefitType, benefitSettings] of Object.entries(
    values.benefitSettings
  )) {
    if (benefitSettings) {
      formattedSettings[benefitType] = {
        ...benefitSettings,
        maxAmountPerPeriod: benefitSettings?.maxAmountPerPeriod
          ? +toDot(benefitSettings?.maxAmountPerPeriod)
          : undefined
      };
    }
  }

  return {
    ...values,
    benefitSettings: formattedSettings
  } as ICreateBenefitGroupForm;
};

export const CreateBenefitGroupForm = ({
  defaultSettings,
  children
}: PropsWithChildren<CreateBenefitGroupFormProps>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [createBenefitGroup, { isLoading }] = useCreateBenefitGroupMutation({
    fixedCacheKey: CREATE_BENEFIT_GROUPS_CACHE_KEY
  });

  const benefitSettings = createDefaultValuesFromSettings(defaultSettings);

  const createBenefitGroupForm = useForm<ICreateBenefitGroupForm>({
    mode: 'onChange',
    defaultValues: {
      groupName: '',
      benefitSettings
    }
  });

  const checkedBenefits = createBenefitGroupForm.watch('benefitSettings');
  const isAnyBenefitChecked = Object.values(checkedBenefits).some(
    benefit => !!benefit
  );

  return (
    <FormProvider {...createBenefitGroupForm}>
      <BenefitGroupForm
        handleSubmitForm={async formValues => {
          const formattedBenefitSettings = formatValuesToDTO(formValues);

          try {
            await createBenefitGroup({
              payload: formattedBenefitSettings
            }).unwrap();
            dispatch(goToPage(routerPaths.benefitGroups));
          } catch (error) {
            dispatch(showNotification({ message: 'messages.error' }, 'error'));
          }
        }}
      >
        <PagePanel
          hasOverflow={false}
          title={t('containers.benefit_groups.create_group_title')}
          footer={{
            submitText: t('containers.benefit_groups.buttons.add'),
            disableSubmit:
              !createBenefitGroupForm.formState.isValid ||
              !isAnyBenefitChecked ||
              isLoading,
            backText: t('containers.benefit_groups.buttons.cancel'),
            onBack: () => {
              dispatch(goToPage(routerPaths.benefitGroups));
            }
          }}
        >
          {children}
        </PagePanel>
      </BenefitGroupForm>
    </FormProvider>
  );
};
